import React from 'react'
import { CardHeader, Heading, Text, Flex, Image, useTooltip, Link } from 'vvs-uikit'
import { Token } from 'vvs-sdk'
import styled from 'styled-components'
import { useTranslation } from 'contexts/Localization'
import { TokenPairImage } from 'components/TokenImage'
import VvsVaultTokenPairImage from '../VvsVaultCard/VvsVaultTokenPairImage'

const Wrapper = styled(CardHeader)<{ isFinished?: boolean; background?: string }>`
  background: ${({ isFinished, background, theme }) =>
    background
      ? theme.colors[background]
      : isFinished
      ? theme.colors.backgroundDisabled
      : 'linear-gradient(89.96deg, #AFCDE0 0.03%, #DABF7B 99.83%)'};
  border-radius: ${({ theme }) => `${theme.radii.card} ${theme.radii.card} 0 0`};
  padding: 12px 24px;
`

const StyledTokenPairImage = styled(TokenPairImage)`
  > div {
    width: 42px;
    top: 50%;
    margin-top: -21px;
    bottom: auto;
  }
`

const StyledVvsVaultTokenPairImage = styled(VvsVaultTokenPairImage)`
  > div {
    width: 42px;
    top: 50%;
    margin-top: -21px;
    bottom: auto;
  }
`

const StyledBadge = styled(Flex)`
  height: 44px;
  width: 109px;
  border-radius: 50px;

  background: #1199fa;
`

const StyledCardHeader: React.FC<{
  earningToken: Token
  stakingToken: Token
  isAutoVault?: boolean
  isFinished?: boolean
  isStaking?: boolean
  suffix?: string
}> = ({ earningToken, stakingToken, suffix, isFinished = false, isAutoVault = false, isStaking = false }) => {
  const { t } = useTranslation()
  const isVvsPool = earningToken.symbol === 'VVS' && stakingToken.symbol === 'VVS'
  const background = earningToken.symbol === 'VVS' ? (isStaking ? 'bubblegum' : 'cardHeader') : 'backgroundAlt3'

  const { targetRef, tooltip, tooltipVisible } = useTooltip(
    <Flex flexDirection="column">
      <span>
        {t('xVVS upgrade is here! This will impact your staked VVS as rewards are gradually moving to xVVS vaults.')}
      </span>
      <Link href="https://medium.com/vvs-finance/xvvs-is-coming-vvs-mechanics-upgrade-3139e9f8386c" target="_blank">
        {t('Read more here. ')}
      </Link>
    </Flex>,
    { placement: 'top-start' },
  )

  const getHeadingPrefix = () => {
    if (isAutoVault) {
      // vault
      return t('Auto')
    }
    if (isVvsPool) {
      // manual vvs
      return t('Manual')
    }
    // all other pools
    return t('Earn')
  }

  const getSubHeading = () => {
    if (isAutoVault) {
      return t('Stake and forget')
    }
    if (isVvsPool) {
      return t('Manual staking')
    }
    return t('Stake %symbol%', { symbol: stakingToken.symbol })
  }

  return (
    <Wrapper isFinished={isFinished} background={background}>
      <Flex alignItems="center" justifyContent="space-between">
        <Flex flexDirection="column">
          <Heading color={earningToken.symbol !== 'VVS' ? 'text' : isFinished ? 'textDisabled' : 'black'} scale="lg">
            {`${getHeadingPrefix()} ${earningToken.symbol}${suffix ? ` ${suffix}` : ''}`}
          </Heading>
          <Text color={earningToken.symbol !== 'VVS' ? 'text' : isFinished ? 'textDisabled' : 'black'} as="h3">
            {getSubHeading()}
          </Text>
        </Flex>
        {isVvsPool ? (
          <>
            {tooltipVisible && tooltip}
            <StyledBadge justifyContent="center" alignItems="center" ref={targetRef} role="button">
              <Image src="/images/info.png" alt="info" width={24} height={24} />
              <Text mt={-1} ml={1} color="white" small bold>
                {t('Upgrade')}
              </Text>
            </StyledBadge>
          </>
        ) : isAutoVault ? (
          <StyledVvsVaultTokenPairImage width={64} height={64} />
        ) : (
          <StyledTokenPairImage primaryToken={stakingToken} secondaryToken={earningToken} width={64} height={64} />
        )}
      </Flex>
    </Wrapper>
  )
}

export default StyledCardHeader
